// Console Edit Code -- single canonical-document helper shared between
// howWeDoItEditor.jsx (the Page Builder) and HowWeDoItCodeEditorModal.jsx
// (the Console). AUTOSAVE_FIELDS/pickEditableFields used to be defined
// privately inside howWeDoItEditor.jsx -- moved here so both sides read the
// exact same field list instead of risking two copies drifting apart.
// Mirrors AUTOSAVE_FIELDS in server_elakay/elakay_controllers/howWeDoIt.js
// (minus "estimatedReadingTime", which is server-derived, never hand-edited).
import { generateBlockId, BLOCK_TYPES } from "./blockRegistry";

export const AUTOSAVE_FIELDS = [
  "schemaVersion",
  "title",
  "slug",
  "shortDescription",
  "fullDescription",
  "coverImage",
  "category",
  "subcategory",
  "language",
  "nativeLanguage",
  "defaultLanguage",
  "translations",
  "mediaTranslations",
  "allowedRoles",
  "visibility",
  "featured",
  "contentBlocks",
  "htmlCode",
  "customCss",
  "customJs",
  "sourceFormat",
  "sourceText",
  "seo",
];

const SOURCE_FORMAT_VALUES = ["", "html", "markdown", "text"];

export const pickEditableFields = (tutorial) => {
  const picked = {};
  AUTOSAVE_FIELDS.forEach((field) => {
    picked[field] = tutorial[field];
  });
  return picked;
};

// THE one place an API response is normalized before any of the 4 pages
// (HowWeDoIt, HowWeDoItDetail, HowWeDoItDashboard, HowWeDoItEditor) reads
// it -- fills in safe defaults for any field an OLDER document might not
// have (a tutorial saved before customCss/customJs existed, for instance),
// so every page always sees the same, fully-shaped canonical document
// regardless of when it was created. Never overwrites a present, correctly-
// typed value -- purely additive/defensive, not a data migration. Returns
// null unchanged so a "not found" response stays "not found".
export const normalizeHowWeDoItDocument = (tutorial) => {
  if (!tutorial || typeof tutorial !== "object") return tutorial ?? null;
  return {
    ...tutorial,
    schemaVersion: 3,
    contentBlocks: Array.isArray(tutorial.contentBlocks) ? tutorial.contentBlocks : [],
    // Independent freeform HTML page field -- see howWeDoItRenderMode.js's
    // hasTutorialHtmlCode()/getHowWeDoItRenderMode(), the single place that
    // decides between this and contentBlocks. Never derived from
    // contentBlocks here -- an older document without this field simply
    // defaults to "" (blocks mode), exactly like customCss/customJs below.
    htmlCode: typeof tutorial.htmlCode === "string" ? tutorial.htmlCode : "",
    customCss: typeof tutorial.customCss === "string" ? tutorial.customCss : "",
    customJs: typeof tutorial.customJs === "string" ? tutorial.customJs : "",
    // Universal Console authoring format + original Markdown/plain-text
    // source (see elakay_models/howWeDoIt.js). Older documents: "" / "".
    sourceFormat: SOURCE_FORMAT_VALUES.includes(tutorial.sourceFormat) ? tutorial.sourceFormat : "",
    sourceText: typeof tutorial.sourceText === "string" ? tutorial.sourceText : "",
    featured: tutorial.featured === true,
    allowedRoles: Array.isArray(tutorial.allowedRoles) ? tutorial.allowedRoles : [],
    seo: isPlainObject(tutorial.seo) ? tutorial.seo : { title: "", description: "", keywords: [] },
    coverImage: isPlainObject(tutorial.coverImage) ? tutorial.coverImage : { url: "", alt: "", caption: "" },
    nativeLanguage: typeof tutorial.nativeLanguage === "string" && tutorial.nativeLanguage ? tutorial.nativeLanguage : (tutorial.language || "fr"),
    defaultLanguage: typeof tutorial.defaultLanguage === "string" && tutorial.defaultLanguage ? tutorial.defaultLanguage : (tutorial.nativeLanguage || tutorial.language || "fr"),
    translations: isPlainObject(tutorial.translations) ? tutorial.translations : {},
    mediaTranslations: isPlainObject(tutorial.mediaTranslations) ? tutorial.mediaTranslations : {},
    localizationRevision: Number(tutorial.localizationRevision) || 0,
  };
};

export class CodeDocumentError extends Error {}

const VALID_BLOCK_TYPES = new Set(BLOCK_TYPES.map((def) => def.type));

const isPlainObject = (value) => !!value && typeof value === "object" && !Array.isArray(value);

// Normalizes one block (top-level or child). `isChild` strips/never reads a
// `children` key -- matches createChildBlock() (blockRegistry.js) and the
// backend's own sanitizeBlock(), both of which cap nesting at one level.
// Existing Mongo subdocument `_id` values are carried through the browser
// round trip. The backend still treats its own current document as the
// authority for those ids and never trusts a client-supplied replacement.
// `order` is always recomputed from array position, same as setBlocks() in
// howWeDoItEditor.jsx and sanitizeBlock() server-side -- a hand-typed
// "order" in the JSON is intentionally never trusted.
const normalizeBlock = (raw, index, path, usedIds, isChild) => {
  if (!isPlainObject(raw)) {
    throw new CodeDocumentError(`${path}: must be an object.`);
  }
  if (typeof raw.type !== "string" || !VALID_BLOCK_TYPES.has(raw.type)) {
    throw new CodeDocumentError(
      `${path}: invalid or missing "type" (got ${JSON.stringify(raw.type)}). Valid types: ${[...VALID_BLOCK_TYPES].join(", ")}.`
    );
  }

  let blockId = typeof raw.blockId === "string" && raw.blockId.trim() ? raw.blockId.trim() : "";
  if (!blockId) blockId = generateBlockId(); // a genuinely new JSON block gets one identity, once
  if (usedIds.has(blockId)) {
    throw new CodeDocumentError(`${path}: duplicate "blockId" ${JSON.stringify(blockId)}.`);
  }
  usedIds.add(blockId);

  const normalized = {
    blockId,
    type: raw.type,
    order: index,
    visible: raw.visible !== false,
    content: isPlainObject(raw.content) ? raw.content : {},
    settings: isPlainObject(raw.settings) ? raw.settings : {},
  };

  if (raw._id !== undefined && raw._id !== null && String(raw._id).trim()) {
    normalized._id = raw._id;
  }

  if (!isChild) {
    const rawChildren = Array.isArray(raw.children) ? raw.children : [];
    normalized.children = rawChildren.map((child, childIndex) =>
      normalizeBlock(child, childIndex, `${path} > child block #${childIndex + 1}`, usedIds, true)
    );
  }

  return normalized;
};

export const normalizeContentBlocks = (blocks) => {
  if (!Array.isArray(blocks)) {
    throw new CodeDocumentError('"contentBlocks" must be an array.');
  }
  const usedIds = new Set();
  return blocks.map((block, index) => normalizeBlock(block, index, `Block #${index + 1}`, usedIds, false));
};

// Validates + normalizes a full editable document from the code editor.
// Only touches keys actually present in `parsed` -- Apply merges the result
// into the live tutorial state with a plain spread, so an omitted key simply
// leaves that field untouched rather than wiping it out.
export const normalizeCodeDocument = (parsed) => {
  if (!isPlainObject(parsed)) {
    throw new CodeDocumentError("The document must be a JSON object.");
  }

  const unknownKeys = Object.keys(parsed).filter((key) => !AUTOSAVE_FIELDS.includes(key));
  if (unknownKeys.length) {
    throw new CodeDocumentError(`Unknown field(s): ${unknownKeys.join(", ")}.`);
  }

  const normalized = { ...parsed };

  if (Object.hasOwn(parsed, "contentBlocks")) {
    normalized.contentBlocks = normalizeContentBlocks(parsed.contentBlocks);
  }
  if (Object.hasOwn(parsed, "allowedRoles") && !Array.isArray(parsed.allowedRoles)) {
    throw new CodeDocumentError('"allowedRoles" must be an array.');
  }
  if (Object.hasOwn(parsed, "seo") && !isPlainObject(parsed.seo)) {
    throw new CodeDocumentError('"seo" must be an object.');
  }
  if (Object.hasOwn(parsed, "coverImage") && !isPlainObject(parsed.coverImage)) {
    throw new CodeDocumentError('"coverImage" must be an object.');
  }
  if (Object.hasOwn(parsed, "translations") && !isPlainObject(parsed.translations)) {
    throw new CodeDocumentError('"translations" must be an object.');
  }
  if (Object.hasOwn(parsed, "mediaTranslations") && !isPlainObject(parsed.mediaTranslations)) {
    throw new CodeDocumentError('"mediaTranslations" must be an object.');
  }
  if (Object.hasOwn(parsed, "htmlCode") && typeof parsed.htmlCode !== "string") {
    throw new CodeDocumentError('"htmlCode" must be a string.');
  }
  if (Object.hasOwn(parsed, "customCss") && typeof parsed.customCss !== "string") {
    throw new CodeDocumentError('"customCss" must be a string.');
  }
  if (Object.hasOwn(parsed, "customJs") && typeof parsed.customJs !== "string") {
    throw new CodeDocumentError('"customJs" must be a string.');
  }
  if (Object.hasOwn(parsed, "sourceText") && typeof parsed.sourceText !== "string") {
    throw new CodeDocumentError('"sourceText" must be a string.');
  }
  if (Object.hasOwn(parsed, "sourceFormat") && !SOURCE_FORMAT_VALUES.includes(parsed.sourceFormat)) {
    throw new CodeDocumentError('"sourceFormat" must be one of "", "html", "markdown", "text".');
  }
  if (Object.hasOwn(parsed, "featured") && typeof parsed.featured !== "boolean") {
    throw new CodeDocumentError('"featured" must be true or false.');
  }

  return normalized;
};

// Best-effort line/column for a JSON.parse SyntaxError, from the "position N"
// most JS engines embed in the message. Returns null when unavailable
// (message format not recognized) -- callers must handle that case.
export const locateJsonErrorPosition = (text, error) => {
  const match = /position (\d+)/.exec(error?.message || "");
  if (!match) return null;
  const pos = Number(match[1]);
  const before = text.slice(0, pos);
  const lines = before.split("\n");
  return { line: lines.length, column: lines[lines.length - 1].length + 1 };
};

// Parses + validates raw editor text in one shot. Never throws -- always
// returns a { valid, ... } result so callers (live status bar, Apply) share
// one code path.
export const validateCodeText = (text) => {
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch (err) {
    const pos = locateJsonErrorPosition(text, err);
    return { valid: false, message: err.message, line: pos?.line, column: pos?.column };
  }
  try {
    return { valid: true, normalized: normalizeCodeDocument(parsed) };
  } catch (err) {
    return { valid: false, message: err.message || String(err) };
  }
};
